import {useCallback, useEffect, type SetStateAction} from "react"

import {useLocalize} from "@agenta/i18n"
import {useObservability} from "@agenta/observability"
import {useTraceDrawer} from "@agenta/observability/traceDrawer"
import {traceDrawerSetQueryParam} from "@agenta/observability/traceDrawer"
import {
    setTraceDrawerActiveSpanAtom,
    setTraceDrawerTraceAtom,
    traceDrawerActiveSpanIdAtom,
} from "@agenta/observability/traceDrawer"
import {EnhancedButton} from "@agenta/ui/components/presentational"
import {ArrowsIn, ArrowsOut, X} from "@phosphor-icons/react"
import {useAtomValue, useSetAtom} from "jotai"
import dynamic from "next/dynamic"

import {SkeletonBlock} from "../primitives/SkeletonBlock"

const TraceContent = dynamic(() => import("./TraceContent"))
const TraceHeader = dynamic(() => import("./TraceHeader"))
const TraceTree = dynamic(() => import("./TraceTree"))

export type TraceDrawerLayout = "split" | "stacked"

interface TraceDrawerContentProps {
    onClose: () => void
    onToggleWidth: () => void
    isExpanded: boolean
    layout?: TraceDrawerLayout
}

const TraceDrawerContent = ({
    onClose,
    onToggleWidth,
    isExpanded,
    layout = "split",
}: TraceDrawerContentProps) => {
    const t = useLocalize()
    const stacked = layout === "stacked"
    const {traces, activeSpanId, getTraceById, traceResponse, error, isLoading, traceId} =
        useTraceDrawer()
    const {
        traceTabs,
        filters,
        sort,
        limit,
        setSelectedTraceId: setGlobalSelectedTraceId,
        setSelectedNode: setGlobalSelectedNode,
    } = useObservability()
    const rawActiveSpanId = useAtomValue(traceDrawerActiveSpanIdAtom)
    const setActiveSpan = useSetAtom(setTraceDrawerActiveSpanAtom)
    const setTraceDrawerTrace = useSetAtom(setTraceDrawerTraceAtom)
    const setTraceQueryParam = useCallback(
        (value: string | null | undefined) => traceDrawerSetQueryParam("trace", value),
        [],
    )
    const setSpanQueryParam = useCallback(
        (value: string | null | undefined) => traceDrawerSetQueryParam("span", value),
        [],
    )

    const activeId = activeSpanId || ""

    // Write the stored id only when it is not a loaded span.
    useEffect(() => {
        if (!traces.length || !activeId) return
        if (rawActiveSpanId === activeId) return
        setActiveSpan(activeId)
    }, [traces.length, activeId, rawActiveSpanId, setActiveSpan])

    useEffect(() => {
        setSpanQueryParam(activeId || null)
    }, [activeId, setSpanQueryParam])

    const setSelected = useCallback(
        (value: SetStateAction<string>) => {
            const next = typeof value === "function" ? value(activeId) : value
            setActiveSpan(next || null)
        },
        [activeId, setActiveSpan],
    )

    const activeTrace = getTraceById(activeId)

    return (
        <div className="h-full w-full flex flex-col" data-tour="trace-drawer">
            <div className="flex items-center gap-3 px-4 py-3 border-0 border-b border-solid border-colorSplit">
                {/* The panel is already as wide as the screen when stacked; widening does nothing. */}
                {stacked ? null : (
                    <EnhancedButton
                        onClick={onToggleWidth}
                        type="text"
                        icon={isExpanded ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />}
                    />
                )}
                <div className="flex-1 min-w-0">
                    <TraceHeader
                        activeTrace={activeTrace as never}
                        activeTraceId={activeId}
                        traceId={traceId}
                        traces={traces as never}
                        traceTabs={traceTabs}
                        filters={filters}
                        sort={sort}
                        limit={limit}
                        setSelectedTraceId={setGlobalSelectedTraceId}
                        setSelectedNode={setGlobalSelectedNode}
                        setTraceParam={setTraceQueryParam}
                        setSpanParam={setSpanQueryParam}
                        setTraceDrawerTrace={setTraceDrawerTrace}
                        activeTraceIndex={0}
                        setSelected={setSelected}
                    />
                </div>
                {/* Close sits last, at the right edge, where the shared SheetHeader puts it. */}
                <EnhancedButton
                    onClick={onClose}
                    type="text"
                    icon={<X size={14} />}
                    aria-label={t("com_ui_close")}
                    data-tour="trace-drawer-close"
                />
            </div>
            {/* antd `Spin` wrapped the body and dimmed it; the tree/content render their own
                skeletons, so a loading pass just shows a block above them. */}
            <div className="flex-1 min-h-0">
                {isLoading ? <SkeletonBlock className="m-3" /> : null}
                <div className="h-full">
                    {/* antd Splitter: a 320px tree column beside the content. Stacked, the tree
                        takes the top of the panel and the content the rest, both full width. */}
                    <div className={stacked ? "flex h-full flex-col" : "flex h-full"}>
                        <div
                            className={
                                stacked
                                    ? "h-[35%] w-full shrink-0 border-0 border-b border-solid border-colorSplit"
                                    : "w-[320px] min-w-[320px] shrink-0 border-0 border-r border-solid border-colorSplit"
                            }
                        >
                            <TraceTree
                                activeTraceId={activeId}
                                selected={activeId}
                                setSelected={setSelected}
                            />
                        </div>
                        <div
                            className={stacked ? "flex-1 min-h-0 min-w-0" : "flex-1 min-w-[400px]"}
                        >
                            <TraceContent
                                activeTrace={activeTrace as never}
                                traceResponse={traceResponse}
                                error={error as never}
                                isLoading={isLoading}
                                setSelectedTraceId={setGlobalSelectedTraceId}
                                traces={traces as never}
                                activeId={activeId}
                                layout={layout}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TraceDrawerContent
